// Utility helpers to ensure <script type="module" src="…"> tags
// found in SSR-injected HTML are actually fetched and executed.
//
// Browsers do not execute scripts added via innerHTML/insertAdjacentHTML.
// These helpers parse the HTML, find external module scripts, and
// programmatically append equivalent <script> tags to <head>, avoiding
// duplicates and returning Promises for load completion.

const loadedScriptsKey = "__mealzLoadedModuleScripts";

function getLoadedSet() {
  const w = typeof window !== "undefined" ? window : globalThis;
  if (!w[loadedScriptsKey]) {
    try {
      Object.defineProperty(w, loadedScriptsKey, {
        value: new Set(),
        writable: false,
        enumerable: false,
        configurable: false
      });
    } catch (_) {
      // Fallback if defineProperty fails
      w[loadedScriptsKey] = new Set();
    }
  }
  return w[loadedScriptsKey];
}

function hasScriptInDom(src) {
  try {
    const scripts = document.querySelectorAll("script[src]");
    return Array.from(scripts).some((s) => s.getAttribute("src") === src);
  } catch (_) {
    return false;
  }
}

function ensureModuleScriptLoaded(src) {
  if (!src || typeof document === "undefined") {
    return Promise.resolve(false);
  }

  const loaded = getLoadedSet();
  if (loaded.has(src) || hasScriptInDom(src)) {
    loaded.add(src);
    return Promise.resolve(false);
  }

  return new Promise((resolve, reject) => {
    try {
      const script = document.createElement("script");
      script.type = "module";
      script.src = src;
      script.onload = () => {
        getLoadedSet().add(src);
        resolve(true);
      };
      script.onerror = (err) => {
        // Still mark as attempted to avoid tight retry loops
        getLoadedSet().add(src);
        reject(err);
      };
      document.head.appendChild(script);
    } catch (err) {
      reject(err);
    }
  });
}

async function loadModuleScriptsFromElement(root) {
  if (!root || typeof root.querySelectorAll !== "function") {
    return [];
  }
  const scripts = Array.from(
    root.querySelectorAll('script[type="module"][src]')
  );
  const uniqueSrcs = Array.from(
    new Set(scripts.map((s) => s.getAttribute("src")).filter(Boolean))
  );
  return await Promise.all(uniqueSrcs.map((src) => ensureModuleScriptLoaded(src)));
}

/**
 * Find and insert module scripts from HTML fetched from SSR API.
 * @param {string} html HTML fetched from SSR API
 * @returns {Promise<void>} Promise that resolves when all module scripts are loaded
 */
export async function loadModuleScriptsFromHtml(html) {
  if (typeof html !== "string" || !html) {
    return [];
  }
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return await loadModuleScriptsFromElement(doc);
  } catch (_) {
    // Fallback: small regex to extract src attributes of module scripts (no closing tag match)
    const srcs = [];
    const re = /<script[^>]*type=["']module["'][^>]*src=["']([^"']+)["'][^>]*>/gi;
    let m;
    while ((m = re.exec(html)) !== null) {
      if (m[1]) srcs.push(m[1]);
    }
    const uniqueSrcs = Array.from(new Set(srcs));
    return await Promise.all(uniqueSrcs.map((src) => ensureModuleScriptLoaded(src)));
  }
}

